type Translate = (source: string, params?: Record<string, string | number>) => string

interface Message {
  id?: number
  text: string
  context?: Record<string, unknown>
}

// IDs and context names are checked against the pinned Kratos v26.2.0:
// https://github.com/ory/kratos/blob/v26.2.0/text/id.go
// 4090001-4090003 are the local passkey-policy validation hook's IDs.
const messages: Record<number, string> = {
  1010001: 'Sign in',
  1010003: 'Please confirm this action by verifying that it is you.',
  1010004: 'Please complete the second authentication challenge.',
  1010005: 'Verify',
  1010006: 'Authentication code',
  1010007: 'Backup recovery code',
  1010013: 'Continue',
  1010014: 'A login code has been sent to your email address. If it does not arrive, check the address and try signing in again.',
  1010015: 'Sign in with code',
  1010017: 'Sign in and link',
  1010019: 'Continue with code',
  1010021: 'Sign in with a Passkey',
  1010022: 'Sign in',
  1040001: 'Sign up',
  1040003: 'Continue',
  1040005: 'A registration code has been sent to your email address. If it does not arrive, check the address and try registering again.',
  1040006: 'Sign up with code',
  1050001: 'Your changes have been saved!',
  1050019: 'Add Passkey',
  1060002: 'A recovery link has been sent to the email address you provided. If it does not arrive, check the address and use the one you registered with.',
  1060003: 'A recovery code has been sent to the email address you provided. If it does not arrive, check the address and use the one you registered with.',
  1070001: 'Password',
  1070003: 'Save',
  1070004: 'ID',
  1070005: 'Submit',
  1070006: 'Verify code',
  1070007: 'Email',
  1070008: 'Resend code',
  1070009: 'Continue',
  1070010: 'Recovery code',
  1070011: 'Verification code',
  1070012: 'Registration code',
  1070013: 'Login code',
  1070014: 'Login and link credential',
  1080001: 'A verification link has been sent to the email address you provided. If it does not arrive, check the address and use the one you registered with.',
  1080002: 'You successfully verified your email address.',
  1080003: 'A verification code has been sent to the email address you provided. If it does not arrive, check the address and use the one you registered with.',
  4000004: 'The value does not match the required format.',
  4000006: 'The provided credentials are invalid, check for spelling mistakes in your password or username, email address, or phone number.',
  4000007: 'An account with the same identifier (email, phone, username, ...) exists already.',
  4000008: 'The provided authentication code is invalid, please try again.',
  4000009: 'Could not find any login identifiers. Did you forget to set them? This could also be caused by a server misconfiguration.',
  4000010: 'Account not active yet. Did you forget to verify your email address?',
  4000013: 'You have no WebAuthn device set up.',
  4000015: 'This account does not exist or has no security key set up.',
  4000027: 'An account with the same identifier already exists. Sign in to your existing account to link your social profile.',
  4000031: 'The password can not be used because it is too similar to the identifier.',
  4000034: 'The password has been found in data breaches and must no longer be used.',
  4000035: 'This account does not exist or has not setup sign in with code.',
  4000036: 'The provided traits do not match the traits previously associated with this flow.',
  4010001: 'This login form has expired. Please try again.',
  4010002: 'Could not find a strategy to log you in with. Did you fill out the form correctly?',
  4010003: 'Could not find a strategy to sign you up with. Did you fill out the form correctly?',
  4010004: 'Could not find a strategy to update your settings. Did you fill out the form correctly?',
  4010005: 'Could not find a strategy to recover your account with. Did you fill out the form correctly?',
  4010006: 'Could not find a strategy to verify your account with. Did you fill out the form correctly?',
  4010007: 'The request was already completed successfully and can not be retried.',
  4010008: 'The login code is invalid or has already been used. Please try again.',
  4010009: 'Linked credentials do not match.',
  4010010: 'The address you entered does not match any known addresses in the current account.',
  4040001: 'This registration form has expired. Please try again.',
  4040002: 'The request was already completed successfully and can not be retried.',
  4040003: 'The registration code is invalid or has already been used. Please try again.',
  4050001: 'This settings form has expired. Please try again.',
  4060001: 'The request was already completed successfully and can not be retried.',
  4060002: 'The recovery flow reached a failure state and must be retried.',
  4060004: 'The recovery token is invalid or has already been used. Please retry the flow.',
  4060005: 'This recovery form has expired. Please try again.',
  4060006: 'The recovery code is invalid or has already been used. Please try again.',
  4070001: 'The verification token is invalid or has already been used. Please retry the flow.',
  4070002: 'The request was already completed successfully and can not be retried.',
  4070003: 'The verification flow reached a failure state and must be retried.',
  4070005: 'This verification form has expired. Please try again.',
  4070006: 'The verification code is invalid or has already been used. Please try again.',
  4090001: 'Create an account with a password first, then add a passkey in account settings.',
  4090002: 'Sign in with an active account and verify your current email before managing passkeys.',
  4090003: 'Passkey verification is temporarily unavailable. Please try again.',
}

const fieldLabels: Record<string, string> = {
  identifier: 'Email', email: 'Email', 'traits.email': 'Email', password: 'Password',
  username: 'Username', 'traits.username': 'Username', name: 'Name', 'traits.name': 'Name',
  'traits.name.first': 'First name', 'traits.name.last': 'Last name', code: 'Code',
}

function fieldLabel(name: string): string | undefined {
  return Object.prototype.hasOwnProperty.call(fieldLabels, name) ? fieldLabels[name] : undefined
}

const parameterMessages: Record<number, { source: string; fields: Record<string, 'string' | 'number'> }> = {
  1010002: { source: 'Sign in with {provider}', fields: { provider: 'string' } },
  1010018: { source: 'Sign in with {provider} and link credential', fields: { provider: 'string' } },
  1040002: { source: 'Sign up with {provider}', fields: { provider: 'string' } },
  1050002: { source: 'Link {provider}', fields: { provider: 'string' } },
  1050003: { source: 'Unlink {provider}', fields: { provider: 'string' } },
  1050020: { source: 'Remove Passkey {display_name}', fields: { display_name: 'string' } },
  4000003: { source: 'Use at least {min_length} characters (currently {actual_length}).', fields: { min_length: 'number', actual_length: 'number' } },
  4000005: { source: 'The password cannot be used: {reason}', fields: { reason: 'string' } },
  4000017: { source: 'Use at most {max_length} characters (currently {actual_length}).', fields: { max_length: 'number', actual_length: 'number' } },
  4000032: { source: 'The password must be at least {min_length} characters long, but got {actual_length}.', fields: { min_length: 'number', actual_length: 'number' } },
  4000033: { source: 'The password must be at most {max_length} characters long, but got {actual_length}.', fields: { max_length: 'number', actual_length: 'number' } },
}

export function translateKratosMessage(message: Message, t: Translate): string {
  const id = message.id
  if (id === undefined) return t(message.text)
  if (typeof id !== 'number') return message.text
  if (messages[id]) return t(messages[id])
  if (id === 1070002) return t(message.text)
  // Kratos v1.1.0 wraps jsonschema v3.0.8 email-format errors as the generic
  // validation ID. Match that exact format; other generic reasons stay intact.
  if (id === 4000001 && /^"(?:[^"\\]|\\.)*" is not valid "email"$/.test(message.text)) {
    return t('Enter a valid email address.')
  }
  if (id === 4000002 && typeof message.context?.property === 'string') {
    const property = message.context.property
    const field = fieldLabel(property) || fieldLabel(`traits.${property}`) || property
    return t('{field} is required.', { field: t(field) })
  }
  if (id === 1060001) {
    // Keep the server-calculated recovery deadline rather than estimating it
    // from the browser clock. Only accept the pinned server's exact template.
    const match = /^You successfully recovered your account\. Please change your password or set up an alternative login method \(e\.g\. social sign in\) within the next (\d+(?:\.\d+)?) minutes\.$/.exec(message.text)
    if (match) return t('Your account has been recovered. Change your password or set up another sign-in method within {minutes} minutes.', { minutes: match[1] })
    return message.text
  }
  const template = parameterMessages[id]
  if (!template) return message.text
  const params: Record<string, string | number> = {}
  for (const [key, type] of Object.entries(template.fields)) {
    const value = message.context?.[key]
    if (type === 'number' && typeof value === 'number' && Number.isFinite(value) && value >= 0) params[key] = value
    else if (type === 'string' && typeof value === 'string') params[key] = key === 'reason' ? t(value) : value
    else return message.text
  }
  return t(template.source, params)
}

export function translateKratosFieldLabel(name: string | undefined, label: Message | undefined, t: Translate): string {
  // The configured identifier is the email address. Other explicit server
  // labels (such as recovery vs verification code) retain their distinction.
  if (name === 'identifier') return t('Email')
  if (label) {
    const translated = translateKratosMessage(label, t)
    if ((typeof label.id === 'number' && messages[label.id]) || translated !== label.text || !name || !fieldLabel(name)) return translated
  }
  return t((name && fieldLabel(name)) || label?.text || name || '')
}
